import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { useCallback, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { JustRightHeightModal } from 'components/styled/scoped';
import Icon from '@ant-design/icons';
import { Input, Alert, message } from 'antd';
import { ReactComponent as LinkSvg } from 'assets/icons/link.svg';
import ShopeeMultiUpload from './ShopeeMultiUpload';
import { queryReturnEvidence, uploadReturnEvidence } from 'services/api/refund';
import type { ExistedEvidence } from 'services/api/refund';
import { useMount } from 'ahooks';
import SiderImageSlide from 'views/Conversation/Sider/SiderImageSlide';
import { PreviewModalProvider } from 'views/Conversation/Sider/PreviewModal';
import useSlide from 'views/Conversation/Sider/useSlide';
import { openExternalURL } from 'utils/openExternalURL';

type SubmitEvidenceModalProps = {
  internalStoreId: string;
  externalStoreId: string;
  externalTradeId: string;
  returnSn: string;
  visible: boolean;
  onCancel: () => void;
};

function SubmitEvidenceModal(props: SubmitEvidenceModalProps) {
  const { visible, onCancel, internalStoreId, returnSn, externalStoreId, externalTradeId } = props;
  const { t } = useTranslate();
  const [images, setImages] = useState<string[]>([]);
  const [description, setDescription] = useState<string>('');
  const [existEvidence, setExistEvidence] = useState<ExistedEvidence>({
    image: [],
    description: '',
    video: [],
  });

  const existedEvidenceLength = existEvidence.image.length + existEvidence.video.length;

  const { index: imageIndex } = useSlide({
    count: 3,
    length: existedEvidenceLength,
    initialIndex: 0,
  });

  useMount(() => {
    queryReturnEvidence({
      returnSn,
      externalStoreId,
      externalTradeId,
    }).then((res) => {
      setExistEvidence(res);
    });
  });

  const handleSubmit = useCallback(async () => {
    try {
      await uploadReturnEvidence({
        returnSn,
        externalStoreId,
        externalTradeId,
        image: images,
        description,
      });
      onCancel();
      message.success(t('api.common.success.messages', { action: t('trade.上传证据') }));
    } catch (e) {
      if (e.response.data.code === 1101) {
        message.error(t('ViewRefund.overdueTime'));
      } else {
        message.error(t('api.common.error.messages', { action: t('trade.上传证据') }));
      }
    }
  }, [images, returnSn, externalStoreId, externalTradeId, description, t, onCancel]);

  return (
    <JustRightHeightModal
      centered
      title={t('trade.上传证据')}
      width={560}
      visible={visible}
      onCancel={onCancel}
      destroyOnClose
      okText={t('trade.上传证据')}
      onOk={handleSubmit}
      okButtonProps={{
        disabled: images.length === 0,
      }}
      className={css`
        .ant-modal-body {
          padding: 12px 14px !important;
        }
      `}
    >
      <Container>
        {existedEvidenceLength === 3 ? (
          <Alert type="warning" message={t('ViewRefund.shopeeEvidenceLimit', { x: 3 })} showIcon />
        ) : (
          <>
            <div className="title" data-require>
              <span>{t('phrase.图片')}</span>
              <span
                onClick={() => openExternalURL('https://shopee.cn/edu/article/10160', '_blank')}
              >
                {t('ViewRefund.linkToSpec')}
                <Icon component={LinkSvg} />
              </span>
            </div>

            <ShopeeMultiUpload
              returnSn={returnSn}
              storeId={internalStoreId}
              onChange={(images) => setImages(images)}
              maxCount={3 - existedEvidenceLength}
            />

            <div className="title">{t('feedback.描述')}</div>
            <div className="description">
              <Input.TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('ViewRefund.evidencePlaceholder')}
                maxLength={256}
                showCount
                rows={4}
              />
            </div>
          </>
        )}
        {!!existedEvidenceLength && (
          <ExistEvidenceView>
            <div className="title">{t('ViewRefund.evidenceHistory')}</div>
            <PreviewModalProvider>
              <SiderImageSlide
                videos={existEvidence?.video?.map((e) => e.videoUrl)}
                images={existEvidence?.image}
                count={3}
                index={imageIndex}
                offsetPercent={2}
              />
            </PreviewModalProvider>
            {existEvidence?.description && <div className="desc">{existEvidence?.description}</div>}
          </ExistEvidenceView>
        )}
      </Container>
    </JustRightHeightModal>
  );
}

export default function useSubmitEvidenceModal(
  internalStoreId: string,
  returnSn: string,
  externalStoreId: string,
  externalTradeId: string,
) {
  const [visible, setVisible] = useState(false);
  const open = useCallback(() => setVisible(true), []);
  const close = useCallback(() => setVisible(false), []);
  const modal = visible && (
    <SubmitEvidenceModal
      internalStoreId={internalStoreId}
      externalStoreId={externalStoreId}
      returnSn={returnSn}
      externalTradeId={externalTradeId}
      visible={visible}
      onCancel={close}
    />
  );

  return [open, modal] as const;
}

const Container = styled.div`
  .title {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    &:not(:first-of-type) {
      margin-top: 16px;
    }

    &[data-require] {
      padding-left: 2px;

      &:before {
        display: inline-block;
        color: #ff4d4f;
        font-size: 14px;
        font-family: SimSun, sans-serif;
        line-height: 1;
        content: '*';
      }
    }

    gap: 2px;

    & > span {
      display: inline-flex;
      align-items: center;
      gap: 4px;

      &:nth-child(2) {
        color: var(--fett-scenario-accent-text);
        cursor: pointer;
        margin-left: 2px;
      }

      & > svg {
        width: 16px;
        height: 16px;
      }
    }
  }
`;

const ExistEvidenceView = styled.div`
  .title {
    margin-top: 15px;
  }
  & > .desc {
    line-height: 20px;
    font-size: 14px;
    color: var(--fett-text-secondary);
    margin-top: 8px;
  }
`;
